import React, { useState, useEffect } from 'react';
import Grid from '@mui/material/Grid';
import Typography from '../../../layouts/theme-onboarding/Typography';
import PreviousInformation from './confirm/PreviousInformation';
import { Fab, Tooltip, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import UpdateIcon from '@mui/icons-material/Update';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import IconButton from '@mui/material/IconButton';
import { initStep1Data } from '../../../utils/object/OpenContract';
import {
  validatePhone,
  validateName,
  validateSalary,
  validateCompany,
} from '../../../utils/regex/Regex';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../redux/create-actions/AlertAction';
import { customerHasCreditCard, MoneyFormatCustom } from '../../../utils/function/Function';
import { makeStyles } from '@mui/styles';
import Color from '../../../layouts/Color';
import { graphGetAllContractByCustomer } from '../../../services/Contract';
import { ProductCardType } from '../../../enum/Enum.ts';
import { relationShip } from '../../../constant/CustomerReferenceType';
import { graphGetProfile, graphUpdateAdditionalInfo } from '../../../services/Profile';
import { graphGetAllOccupations, graphGetAllPositions } from '../../../services/Onboarding';
import { closeLoadingApi, openLoadingApi } from '../../../redux/create-actions/LoadingAction';

const useStyles = makeStyles(function (theme) {
  return {
    gridContainer: {
      marginTop: "10px",
      [theme.breakpoints.up("xl")]: {
        paddingLeft: ' 200px',
        paddingRight: '200px',
        marginTop: "20px"
      },
    },
  };
});

const info = { textAlign: 'left', alignItem: 'center' };
const minStyle = { minWidth: 120 };
const buttonUpdate = {
  '&:hover': { background: Color.Button, color: Color.Dangerous }
};

export default function Confirmation(props) {
  const theme = useTheme()
  const classes = useStyles();
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));
  const matchDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const [refPhoneErrorText, setRefPhoneErrorText] = useState('');
  const [refNameErrorText, setRefNameErrorText] = useState('');
  const [companyErrorText, setCompanyErrorText] = useState('');
  const [salaryErrorText, setSalaryErrorText] = useState('');
  const [step1, setStep1] = useState(null);
  const [hasCreditCard, setHasCreditCard] = useState(false);
  const [occupationList, setOccupationList] = useState([]);
  const [positionList, setPositionList] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [isChange, setIsChange] = useState(false);
  const [preData, setPreData] = useState(null);

  const isPrepaidType = props.step0Data.productType === ProductCardType.PREPAID;

  const dispatch = useDispatch();

  const checkChangeValue = (data, field) => {
    if (preData[field].toString() !== data?.toString()) {
      setIsChange(true);
    }
    else {
      setIsChange(JSON.stringify(preData) === JSON.stringify(step1));
    }
  }

  const handleUpdateInfo = () => {
    let alert = {
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center',
      },
      isOpen: true,
      severity: 'error',
      title: 'Error',
      message: '',
    };

    if (isEdit) {
      graphUpdateAdditionalInfo({
        company: step1.company,
        salary: step1.salary,
        occupationId: step1.occupationId,
        positionId: step1.positionId,
      })
        .then(res => {
          dispatch(showAlert({
            ...alert,
            severity: 'success',
            title: 'Success',
            message: 'Update additional information successful!',
          }));
          setPreData(step1);
        })
        .catch(error => {
          const err = JSON.parse(JSON.stringify(error))
          alert = { ...alert, message: err.response.errors[0].message };
          dispatch(showAlert(alert));
        })
      setIsEdit(false);
      setIsChange(false);
    }
    else {
      setIsEdit(true);
    }
  }

  function handleValidatePhone(phone) {
    let result = validatePhone(phone);
    let step1Data;
    if (!result) {
      step1Data = { ...step1, refPhone: phone, isRefPhoneError: true };
      setStep1(step1Data);
      checkChangeValue(phone, 'refPhone');
      setRefPhoneErrorText('Error Reference Phone Format');
    } else {
      step1Data = { ...step1, refPhone: phone, isRefPhoneError: false };
      setStep1(step1Data);
      checkChangeValue(phone, 'refPhone');
      setRefPhoneErrorText('');
    }
    return step1Data;
  }

  function handleValidateName(name) {
    let result = validateName(name);
    let step1Data;
    if (!result) {
      step1Data = { ...step1, refName: name, isRefNameError: true };
      setStep1(step1Data);
      checkChangeValue(name, 'refName');
      setRefNameErrorText('Error Reference Name Format');
    } else {
      step1Data = { ...step1, refName: name, isRefNameError: false };
      setStep1(step1Data);
      checkChangeValue(name, 'refName');
      setRefNameErrorText('');
    }
    return step1Data;
  }

  const handleValidateCompany = (company) => {
    let result = validateCompany(company);
    let step1Data;
    if (!result) {
      step1Data = { ...step1, company: company, isCompanyError: true };
      setStep1(step1Data);
      checkChangeValue(company, 'company');
      setCompanyErrorText('Error Company Name Format');
    } else {
      step1Data = { ...step1, company: company, isCompanyError: false };
      setStep1(step1Data);
      checkChangeValue(company, 'company');
      setCompanyErrorText('');
    }
    return step1Data;
  }

  const handleValidateSalary = (salary) => {
    let result = validateSalary(salary);
    let step1Data;
    if (!result) {
      step1Data = { ...step1, salary: salary, isSalaryError: true };
      setStep1(step1Data);
      checkChangeValue(salary, 'salary');
      setSalaryErrorText('Error Salary Format');
    } else {
      step1Data = { ...step1, salary: salary, isSalaryError: false };
      setStep1(step1Data);
      checkChangeValue(salary, 'salary');
      setSalaryErrorText('');
    }
    return step1Data;
  }

  const handleChangeReferenceName = (event) => {
    let value = event.target.value;
    let step1Data = handleValidateName(value);
    props.onUpdateStep1Data(step1Data);
  };

  const handleChangeReferencePhone = (event) => {
    let value = event.target.value;
    let step1Data = handleValidatePhone(value);
    props.onUpdateStep1Data(step1Data);
  };

  const handleChangeRefRelationship = (event) => {
    let value = event.target.value;
    let step1Data = { ...step1, relationship: value };
    checkChangeValue(value, 'relationship');
    setStep1(step1Data);
    props.onUpdateStep1Data(step1Data);
  };

  const handleChangeReferenceNote = (event) => {
    let value = event.target.value;
    let step1Data = { ...step1, refNote: value };
    checkChangeValue(value, 'refNote');
    setStep1(step1Data);
    props.onUpdateStep1Data(step1Data);
  };

  const handleChangeOccupation = (event) => {
    let value = event.target.value;
    let occupationId = occupationList.find(item => item.id === value).occupationId;
    let step1Data = { ...step1, occupation: value, occupationId: occupationId };
    checkChangeValue(occupationId, 'occupationId');
    setStep1(step1Data);
    props.onUpdateStep1Data(step1Data);
  }

  const handleChangePosition = (event) => {
    let value = event.target.value;
    let positionId = positionList.find(item => item.id === value).positionId;
    let step1Data = { ...step1, position: value, positionId: positionId };
    checkChangeValue(positionId, 'positionId');
    setStep1(step1Data);
    props.onUpdateStep1Data(step1Data);
  }

  const handleChangeCompany = (event) => {
    let value = event.target.value;
    let step1Data = handleValidateCompany(value);
    setStep1(step1Data);
    props.onUpdateStep1Data(step1Data);
  }

  const handleChangeSalary = (event) => {
    let value = event.target.value;
    let step1Data = handleValidateSalary(value);
    setStep1(step1Data);
    props.onUpdateStep1Data(step1Data);
  }

  useEffect(() => {
    graphGetAllContractByCustomer()
      .then((res) => {
        setHasCreditCard(customerHasCreditCard(res.items));
      })
      .catch((err) => {
        console.log('Error when get check exist credit card: ', err);
      });
  }, []);

  useEffect(() => {
    let alert = {
      anchorOrigin: {
        vertical: 'top',
        horizontal: 'center',
      },
      isOpen: true,
      severity: 'error',
      title: 'Error',
      message: '',
    };
    if (step1 === null) {
      dispatch(openLoadingApi());
      let customerInformation = graphGetProfile();
      customerInformation
        .then((result) => {
          let data = result;
          let step1Data;
          if (data.customerReference !== null) {
            step1Data = new initStep1Data(
              data.occupation.occupationName,
              data.occupation.id,
              data.position.positionName,
              data.position.id,
              data.salary,
              data.company,
              data.customerReference.name,
              data.customerReference.phoneNumber,
              data.customerReference.referenceType,
              data.customerReference.referenceNote,
            );
          } else {
            step1Data = new initStep1Data(
              data.occupation.occupationName,
              data.occupation.id,
              data.position.positionName,
              data.position.id,
              data.salary,
              data.company,
              '',
              '',
              '',
              '',
            );
          }
          dispatch(closeLoadingApi());
          setStep1(step1Data);
          setPreData(step1Data);
          props.onUpdateStep1Data(step1Data);
        })
        .catch(function (error) {
          alert = { ...alert, message: error };
          dispatch(closeLoadingApi());
          dispatch(showAlert(alert));
        });
    }
  }, [dispatch, step1, props]);

  useEffect(() => {
    graphGetAllOccupations({
      "filter": {
        "isDelete": 0
      },
      "options": {
        "fetchAll": null
      }
    })
      .then(res => {
        let data = res.items.map((item) => {
          return {
            id: item.occupationName,
            name: item.occupationName,
            occupationId: item.id,
          }
        })
        setOccupationList(data);
      })
      .catch(err => {
        console.log("Error when get check exist credit card: ", err)
      })

    graphGetAllPositions({
      "filter": {
        "isDelete": 0
      },
      "options": {
        "fetchAll": true
      }
    })
      .then(res => {
        let data = res.items.map((item) => {
          return {
            id: item.positionName,
            name: item.positionName,
            positionId: item.id,
          }
        })
        setPositionList(data);
      })
      .catch(err => {
        console.log("Error when get check exist credit card: ", err);
      })
  }, [])

  const renderOption = (menu) => {
    return menu.map(
      (item, i) => {
        return (
          <MenuItem value={item.id} key={i}>
            {item.name}
          </MenuItem>
        );
      },
    );
  }

  const renderReferenceComponent = (product, confirmData) => {
    if (product.productType === ProductCardType.CREDIT && hasCreditCard && confirmData !== null) {
      return (
        <React.Fragment>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextField
              required
              id="refname"
              name="Refname"
              label="Reference Name"
              fullWidth
              autoComplete="given-name"
              variant="outlined"
              value={confirmData?.refName}
              error={confirmData.isRefNameError}
              helperText={refNameErrorText}
              onChange={handleChangeReferenceName}
              disabled={!isEdit}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextField
              required
              id="refPhone"
              name="RefPhone"
              label="Reference Phone"
              fullWidth
              autoComplete="given-name"
              variant="outlined"
              value={confirmData?.refPhone}
              error={confirmData.isRefPhoneError}
              helperText={refPhoneErrorText}
              onChange={handleChangeReferencePhone}
              disabled={!isEdit}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <FormControl sx={minStyle} fullWidth disabled={!isEdit}>
              <InputLabel id="demo-simple-select-required-label">
                Relationship *
              </InputLabel>
              <Select
                required
                labelId="demo-simple-select-required-label"
                id="demo-simple-select-required"
                value={confirmData.relationship ? confirmData.relationship : ''}
                label="Relationship "
                onChange={handleChangeRefRelationship}
              >
                {renderOption(relationShip)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <FormControl sx={minStyle} fullWidth>
              <TextField
                id="outlined-multiline-static"
                label="Reference Note"
                multiline
                rows={1}
                value={confirmData.refNote}
                onChange={handleChangeReferenceNote}
                InputLabelProps={{ shrink: true }}
                disabled={!isEdit}
              />
            </FormControl>
          </Grid>
        </React.Fragment>
      );
    } else if (
      product.productType === ProductCardType.CREDIT &&
      !hasCreditCard &&
      confirmData !== null
    ) {
      return (
        <React.Fragment>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextField
              required
              id="refname"
              name="Refname"
              label="Reference Name"
              fullWidth
              autoComplete="given-name"
              variant="standard"
              value={confirmData?.refName}
              error={confirmData.isRefNameError}
              helperText={refNameErrorText}
              onChange={handleChangeReferenceName}
              disabled={!isEdit}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <TextField
              required
              id="refPhone"
              name="RefPhone"
              label="Reference Phone"
              fullWidth
              autoComplete="given-name"
              variant="standard"
              value={confirmData?.refPhone}
              error={confirmData.isRefPhoneError}
              helperText={refPhoneErrorText}
              onChange={handleChangeReferencePhone}
              disabled={!isEdit}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <FormControl sx={minStyle} fullWidth disabled={!isEdit}>
              <InputLabel id="demo-simple-select-required-label">
                Relationship *
              </InputLabel>
              <Select
                required
                labelId="demo-simple-select-required-label"
                id="demo-simple-select-required"
                label="Relationship "
                value={confirmData.relationship ? confirmData.relationship : ''}
                onChange={handleChangeRefRelationship}
              >
                {renderOption(relationShip)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={6} lg={6}>
            <FormControl sx={minStyle} fullWidth>
              <TextField
                id="outlined-multiline-static"
                label="Reference Note"
                multiline
                onChange={handleChangeReferenceNote}
                disabled={!isEdit}
              />
            </FormControl>
          </Grid>
        </React.Fragment>
      );
    } else {
      return <React.Fragment></React.Fragment>;
    }
  };

  const renderConfirmInformation = (product, data) => {
    const isPrepaidType = product.productType === ProductCardType.PREPAID;
    return data !== null ? (
      <React.Fragment>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          {
            isPrepaidType
              ? <TextField
                required
                id="occupation"
                name="occupation"
                label="Occupation"
                fullWidth
                disabled
                autoComplete="given-name"
                value={data.occupation}
              />
              : <FormControl sx={{ ...minStyle, textAlign: 'left' }} fullWidth disabled={!isEdit}>
                <InputLabel id="demo-simple-select-required-label">
                  Occupation *
                </InputLabel>
                <Select
                  required
                  labelId="demo-simple-select-required-label"
                  id="demo-simple-select-required"
                  value={occupationList.length === 0 ? '' : data?.occupation}
                  label="Occupation"
                  onChange={handleChangeOccupation}
                >
                  {renderOption(occupationList)}
                </Select>
              </FormControl>}
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          {
            isPrepaidType
              ? <TextField
                required
                id="position"
                name="position"
                label="Position"
                fullWidth
                disabled
                autoComplete="given-name"
                value={data.position}
              />
              : <FormControl sx={{ ...minStyle, textAlign: 'left' }} fullWidth disabled={!isEdit}>
                <InputLabel id="demo-simple-select-required-label">
                  Position *
                </InputLabel>
                <Select
                  required
                  labelId="demo-simple-select-required-label"
                  id="demo-simple-select-required"
                  value={positionList.length === 0 ? '' : data?.position}
                  label="Position"
                  onChange={handleChangePosition}
                >
                  {renderOption(positionList)}
                </Select>
              </FormControl>}
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <TextField
            required
            id="company"
            name="company"
            label="Company"
            fullWidth
            autoComplete="given-name"
            disabled={isPrepaidType || !isEdit}
            value={data?.company}
            onChange={handleChangeCompany}
            error={step1.isCompanyError}
            helperText={companyErrorText}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6}>
          <TextField
            required
            id="salary"
            name="salary"
            label="Salary"
            fullWidth
            autoComplete="given-name"
            disabled={isPrepaidType || !isEdit}
            value={data?.salary}
            onChange={handleChangeSalary}
            error={step1.isSalaryError}
            helperText={salaryErrorText}
            InputProps={{
              inputComponent: MoneyFormatCustom
            }}
          />
        </Grid>
      </React.Fragment>
    ) : (
      <React.Fragment></React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Grid container spacing={3} className={classes.gridContainer}>
        <Grid item xs={12} sm={12} md={11} sx={info}>
          <Typography variant="h4" color={Color.Main}> Review previous information and provide additional information: </Typography>
        </Grid>
        {
          !(isPrepaidType || matchDownMd)
          && <Grid item xs={12} sm={12} md={1} sx={{ textAlign: 'center' }}>
            <Tooltip title={isEdit ? 'Edit information' : 'Update information'} placement='top'>
              <div>
                <IconButton
                  sx={{ ...buttonUpdate, padding: 0 }}
                  variant="contained"
                  color='info'
                  onClick={handleUpdateInfo}
                  children={isEdit ? <UpdateIcon /> : <BorderColorIcon />}
                  disabled={isEdit && !isChange}
                />
              </div>
            </Tooltip>
          </Grid>
        }
        <Grid item xs={12} sm={12} md={4}>
          <PreviousInformation step0Data={props.step0Data}></PreviousInformation>
        </Grid>
        <Grid item xs={12} sm={12} md={8}>
          <Grid container spacing={3}>
            {renderConfirmInformation(props.step0Data, step1)}
            {renderReferenceComponent(props.step0Data, step1)}
          </Grid>
        </Grid>
        {
          !(isPrepaidType || !matchDownMd)
          && <Grid item xs={12} sx={{ textAlign: 'end' }}>
            <Tooltip title={!isEdit ? 'Edit information' : 'Update information'} placement='left'>
              <div style={{ position: 'fixed', bottom: '30px', right: '30px' }}>
                <Fab
                  sx={{ ...buttonUpdate }}
                  color='primary'
                  size={matchDownSm ? 'small' : 'medium'}
                  variant="contained"
                  onClick={handleUpdateInfo}
                  aria-label='edit-update'
                  disabled={isEdit && !isChange}
                >
                  {isEdit
                    ? <UpdateIcon fontSize={matchDownSm ? 'small' : 'medium'} />
                    : <BorderColorIcon fontSize={matchDownSm ? 'small' : 'medium'} />
                  }
                </Fab>
              </div>
            </Tooltip>
          </Grid>
        }
      </Grid>
    </React.Fragment>
  );
}
